/**
 * Phase 4 sensitive-command dialog (OD-41 / OD-38 pattern, shared by every money action): the summary
 * of what will happen, a mandatory reason, explicit confirmation, and the admin TOTP step-up collected
 * in place when the server answers STEP_UP_REQUIRED (then the same command is retried with the SAME
 * Idempotency-Key, so a retry can never post twice). The dialog never computes money — it shows the
 * server's preview and sends decimal strings.
 */
import { Button, Dialog, TextField } from '@hopex/ui';
import { useState, type ReactNode } from 'react';
import { api, ApiFailure, errorMessage } from '../api.ts';

export type CommandTone = 'primary' | 'positive' | 'caution' | 'danger';

export function CommandDialog({
  title,
  children,
  confirmLabel = 'تأیید و اعمال',
  tone = 'primary',
  canSubmit = true,
  reasonRequired = true,
  run,
  onClose,
  onDone,
  ...rest
}: {
  title: string;
  children?: ReactNode;
  confirmLabel?: string;
  tone?: CommandTone;
  canSubmit?: boolean;
  reasonRequired?: boolean;
  /** Executes the command with the final reason; throws ApiFailure on refusal. */
  run: (reason: string) => Promise<void>;
  onClose: () => void;
  onDone: () => void;
} & Record<`data-${string}`, unknown>) {
  const [reason, setReason] = useState('');
  const [stepUp, setStepUp] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonOk = !reasonRequired || reason.trim().length >= 3;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (stepUp) {
        await api.post('/auth/step-up', { code });
        setStepUp(false);
        setCode('');
      }
      await run(reason.trim());
      onDone();
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'STEP_UP_REQUIRED') setStepUp(true);
      else setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      isOpen
      onOpenChange={(o) => !o && onClose()}
      title={title}
      isDismissDisabled={busy}
      footer={
        <>
          <Button variant="ghost" onPress={onClose} isDisabled={busy}>
            انصراف
          </Button>
          <Button
            variant={tone}
            loading={busy}
            isDisabled={!canSubmit || !reasonOk || (stepUp && code.length !== 6)}
            onPress={() => void submit()}
            data-command-confirm
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-fg" {...rest}>
        {children}
        {reasonRequired && (
          <TextField
            label="دلیل (الزامی، برای رد ممیزی)"
            value={reason}
            onChange={setReason}
            description="حداقل ۳ نویسه. دلیل در رد ممیزی ثبت می‌شود و قابل ویرایش نیست."
            data-command-reason
          />
        )}
        {stepUp && (
          <div className="rounded-md border border-accent-soft p-3" data-admin-step-up>
            <p className="m-0 mb-2 text-[13px]">
              برای این اقدام، کد فعلی برنامه‌ی Authenticator مدیر را وارد کنید.
            </p>
            <TextField
              label="کد ورود دومرحله‌ای مدیر"
              value={code}
              onChange={setCode}
              ltr
              inputMode="numeric"
            />
          </div>
        )}
        {error && (
          <p role="alert" className="m-0 text-[13px] text-negative" data-command-error>
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** Compact definition list used inside command dialogs (what / who / before → after). */
export function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-md border border-line bg-surface-1 p-3 text-[13px]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-fg-muted">{k}</dt>
          <dd className="m-0 min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
